import { useEffect, useState } from "react";
import { get, post } from "@/lib/api";
import { journeyById, canPerform, allowedRoleNames } from "@/data/canvas";
import { usePersona } from "@/context/PersonaContext";
import { JourneyShell } from "@/components/JourneyShell";
import { GovernedActionPanel } from "@/components/GovernedActionPanel";
import { LayerTag } from "@/components/LayerTag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Info, Plus, Check } from "lucide-react";

const journey = journeyById("room_hire");
const PIPELINE = ["enquiry", "eligibility_checked", "quoted", "approved"];
const PIPE_LABEL = { enquiry: "Enquiry", eligibility_checked: "Eligibility", quoted: "Quote", approved: "Approved", refused: "Refused" };

export default function RoomHireJourney() {
  const { persona } = usePersona();
  const [list, setList] = useState([]);
  const [selId, setSelId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ hirer_name: "", contact_org: "", room: "", requested_date: "", attendees: 10, purpose: "" });

  const load = async () => {
    const d = await get("/mill/bookings");
    setList(d.bookings);
    setSelId((c) => c || d.bookings[0]?.id);
  };
  useEffect(() => { load(); get("/mill/rooms").then((d) => setRooms(d.rooms)); }, []);

  const sel = list.find((b) => b.id === selId);
  const canEnquire = canPerform(persona.id, "room_hire.enquire");
  const canManage = canPerform(persona.id, "room_hire.approve");

  const act = async (action, extra = {}) => {
    setBusy(true);
    try {
      const updated = await post(`/mill/bookings/${sel.id}/action`, { action, ...extra });
      setList((l) => l.map((x) => (x.id === updated.id ? updated : x)));
      setQuote("");
      toast.success(`Booking updated: ${action}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Action failed.");
    }
    setBusy(false);
  };

  const submitEnquiry = async () => {
    if (!form.hirer_name || !form.room || !form.requested_date || !form.purpose) { toast.error("Please complete the enquiry."); return; }
    setBusy(true);
    const created = await post("/mill/bookings", { ...form, attendees: Number(form.attendees) });
    setList((l) => [created, ...l]);
    setSelId(created.id);
    setShowForm(false);
    setForm({ hirer_name: "", contact_org: "", room: "", requested_date: "", attendees: 10, purpose: "" });
    toast.success("Enquiry submitted via web self-service.");
    setBusy(false);
  };

  return (
    <JourneyShell journey={journey}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        {/* Bookings list + new enquiry */}
        <div className="space-y-3" data-testid="bookings-list">
          <div className="flex items-center justify-between px-1">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-mill-muted">Bookings</p>
            <button
              data-testid="new-enquiry-toggle"
              onClick={() => setShowForm((s) => !s)}
              disabled={!canEnquire}
              title={canEnquire ? "New enquiry" : `Only a ${allowedRoleNames("room_hire.enquire")} can enquire`}
              className="inline-flex items-center gap-1 rounded-full bg-mill-moss px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" /> New
            </button>
          </div>
          {!canEnquire && (
            <p className="flex items-center gap-1.5 px-1 text-[11px] text-mill-muted"><Info className="h-3 w-3" /> Switch to Room Hirer to raise an enquiry.</p>
          )}

          {showForm && canEnquire && (
            <div className="space-y-2 rounded-xl border border-mill-moss/40 bg-white p-3" data-testid="enquiry-form">
              <Input placeholder="Your name" value={form.hirer_name} onChange={(e) => setForm({ ...form, hirer_name: e.target.value })} data-testid="enquiry-name" />
              <Input placeholder="Group / organisation" value={form.contact_org} onChange={(e) => setForm({ ...form, contact_org: e.target.value })} data-testid="enquiry-org" />
              <Select value={form.room} onValueChange={(v) => setForm({ ...form, room: v })}>
                <SelectTrigger data-testid="enquiry-room"><SelectValue placeholder="Choose a room" /></SelectTrigger>
                <SelectContent>{rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="date" value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} data-testid="enquiry-date" />
              <Input type="number" placeholder="Attendees" value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} data-testid="enquiry-attendees" />
              <Input placeholder="Purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="enquiry-purpose" />
              <Button className="w-full bg-mill-moss hover:bg-mill-moss-dark" onClick={submitEnquiry} disabled={busy} data-testid="enquiry-submit">Submit enquiry</Button>
            </div>
          )}

          {list.map((b) => (
            <button
              key={b.id}
              data-testid={`booking-select-${b.id}`}
              onClick={() => setSelId(b.id)}
              className={`w-full rounded-xl border p-3 text-left transition-colors ${selId === b.id ? "border-mill-moss bg-white shadow-sm" : "border-stone-200 bg-white/60 hover:bg-white"}`}
            >
              <p className="font-semibold text-mill-ink">{b.hirer_name}</p>
              <p className="text-xs text-mill-muted">{b.room} · {b.requested_date}</p>
              <span className="mt-2 inline-block rounded-full bg-mill-alt px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mill-muted">{PIPE_LABEL[b.status] || b.status}</span>
            </button>
          ))}
        </div>

        {/* Detail */}
        {sel && (
          <div className="space-y-6" data-testid="booking-detail">
            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-xl font-semibold text-mill-ink">{sel.hirer_name}</h2>
                  <p className="text-sm text-mill-muted">{sel.contact_org}</p>
                </div>
                <LayerTag layer="ordinary" />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <Field label="Room" value={sel.room} />
                <Field label="Date" value={sel.requested_date} />
                <Field label="Attendees" value={sel.attendees} />
                <Field label="Purpose" value={sel.purpose} />
              </div>

              {/* Pipeline stepper */}
              <div className="mt-5 flex flex-wrap items-center gap-1.5" data-testid="booking-stepper">
                {PIPELINE.map((st, i) => {
                  const idx = PIPELINE.indexOf(sel.status);
                  const done = sel.status === "refused" ? st === "enquiry" : idx >= i;
                  return (
                    <span key={st} className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${done ? "bg-mill-moss text-white" : "bg-mill-alt text-mill-muted"}`}>
                        {done && <Check className="h-3 w-3" />}{PIPE_LABEL[st]}
                      </span>
                      {i < PIPELINE.length - 1 && <span className="text-stone-300">›</span>}
                    </span>
                  );
                })}
              </div>

              {/* Quote / invoice info */}
              <div className="mt-4 flex flex-wrap gap-4 rounded-xl bg-mill-alt px-4 py-3 text-sm">
                <span className="text-mill-ink">Quote: <strong>{sel.quote_amount != null ? `£${sel.quote_amount.toFixed(2)}` : "—"}</strong></span>
                <span className="text-mill-ink">Terms accepted: <strong>{sel.terms_accepted ? "Yes" : "No"}</strong></span>
                <span className="text-mill-ink">Invoice: <strong className="capitalize">{sel.invoice_status.replace("_", " ")}</strong></span>
              </div>

              {/* Admin actions */}
              <div className="mt-5 border-t border-stone-200 pt-5">
                {!canManage ? (
                  <p className="flex items-center gap-1.5 text-xs text-mill-muted" data-testid="booking-guard"><Info className="h-3.5 w-3.5" /> Only the {allowedRoleNames("room_hire.approve")} can check, quote, approve and invoice. Switch to Centre Administrator.</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2" data-testid="booking-actions">
                    {sel.status === "enquiry" && <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" disabled={busy} onClick={() => act("check")} data-testid="action-check">Check eligibility</Button>}
                    {sel.status === "eligibility_checked" && (
                      <div className="flex items-center gap-2">
                        <Input type="number" placeholder="£ amount" value={quote} onChange={(e) => setQuote(e.target.value)} className="w-28" data-testid="quote-input" />
                        <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" disabled={busy || !quote} onClick={() => act("quote", { quote_amount: Number(quote) })} data-testid="action-quote">Send quote</Button>
                      </div>
                    )}
                    {sel.status === "quoted" && (
                      <>
                        <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" disabled={busy} onClick={() => act("approve")} data-testid="action-approve">Approve & accept terms</Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => act("refuse")} data-testid="action-refuse">Refuse</Button>
                      </>
                    )}
                    {sel.status === "approved" && sel.invoice_status === "not_raised" && <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" disabled={busy} onClick={() => act("invoice")} data-testid="action-invoice">Raise invoice</Button>}
                    {sel.invoice_status === "invoiced" && <Button size="sm" variant="outline" disabled={busy} onClick={() => act("pay")} data-testid="action-pay">Mark paid</Button>}
                    {(sel.status === "approved" && sel.invoice_status === "paid") && <span className="text-sm font-semibold text-mill-moss">Booking complete.</span>}
                  </div>
                )}
              </div>
            </div>

            <GovernedActionPanel
              journey="room_hire"
              refId={sel.id}
              action="room_hire.sign"
              governed={journey.governed}
              disabledReason={sel.status !== "approved" ? "This receipt becomes available once the booking is approved and terms accepted." : null}
            />
          </div>
        )}
      </div>
    </JourneyShell>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-mill-muted">{label}</p>
      <p className="font-medium text-mill-ink">{value}</p>
    </div>
  );
}
